import type { V2Finding } from './finding';
import type { V2ChecklistItem } from './inspection';

// Defect severity — three tiers (Owner decision list 2026-10-19, item 16 / question 9):
//   safety       red     安全 / 立即处理        a safety hazard, or a condition that needs prompt attention
//   repair       orange  修理 / 进一步评估      needs repair, or evaluation by the named professional
//   maintenance  blue    维护                  routine maintenance or a minor condition
// Every defect carries one. The inspector chooses it on the row; until then the row reads the SUGGESTED
// tier below (rules library: what the row checks). InspectorPro's consistency rule — once one defect is
// tiered, every defect is — is met because a defect is never printed without a tier.
// Colours are fixed (the summary page and every defect card use them); the names can be renamed by the
// inspector in Settings (src/state/preferences.ts severityLabels).
export type DefectSeverity = 'safety' | 'repair' | 'maintenance';

export const DEFECT_SEVERITY_ORDER: readonly DefectSeverity[] = Object.freeze(['safety', 'repair', 'maintenance']);

export function isDefectSeverity(value: unknown): value is DefectSeverity {
  return typeof value === 'string' && (DEFECT_SEVERITY_ORDER as readonly string[]).includes(value);
}

export const DEFECT_SEVERITY_COLORS: Readonly<Record<DefectSeverity, string>> = Object.freeze({
  safety: '#ba1a1a',
  repair: '#b45309',
  maintenance: '#1d4ed8'
});

// Rows whose defect is a safety matter by what they check: GFCI / AFCI, smoke and CO alarms, egress,
// stairs and handrails, gas, venting of combustion appliances, relief valves, exposed electrical,
// garage-door reversal and fire separation.
const SAFETY_TEMPLATES: ReadonlySet<string> = new Set([
  'attic-wiring',
  'bath-gfci',
  'bed-alarm',
  'bed-egress',
  'bsmt-alarm',
  'bsmt-gfci',
  'bsmt-stairs',
  'elec-gfci-afci',
  'elec-grounding',
  'elec-panel-interior',
  'elec-service-entrance',
  'ext-entry-steps',
  'ext-gfci',
  'garage-door-reverse',
  'garage-gfci',
  'garage-separation',
  'hvac-boiler-relief',
  'hvac-venting',
  'int-alarms',
  'int-stairs',
  'kit-gas',
  'kit-gfci',
  'plb-fuel-shutoff',
  'safe-alarms-system',
  'safe-egress',
  'safe-electrical-hazards',
  'safe-garage-door',
  'safe-handrails',
  'safe-tpr',
  'safe-trip-fall',
  'wh-combustion-air',
  'wh-tpr',
  'wh-venting'
]);

// Rows whose defect is usually upkeep: finishes, gutters, filters, cabinets.
const MAINTENANCE_TEMPLATES: ReadonlySet<string> = new Set([
  'bath-surfaces',
  'bed-closet',
  'bed-surfaces',
  'ext-gutters',
  'hvac-filter',
  'int-doors',
  'int-surfaces',
  'kit-cabinets-counters',
  'kit-surfaces',
  'roof-gutter-attachment'
]);

// The tier a defect row reads until the inspector chooses one. A confirmed structural concern is a
// referral to a Professional Engineer — repair / further evaluation, never "maintenance".
export function suggestedRowSeverity(item: Pick<V2ChecklistItem, 'templateId' | 'structuralConcern'>): DefectSeverity {
  if (item.templateId !== null && SAFETY_TEMPLATES.has(item.templateId)) return 'safety';
  if (item.structuralConcern) return 'repair';
  if (item.templateId !== null && MAINTENANCE_TEMPLATES.has(item.templateId)) return 'maintenance';
  return 'repair';
}

export function rowSeverity(item: Pick<V2ChecklistItem, 'templateId' | 'structuralConcern' | 'severity'>): DefectSeverity {
  return item.severity ?? suggestedRowSeverity(item);
}

// An other finding's tier follows its class (the inspector already chose the class): a safety finding is
// red, a defect orange, maintenance and advisory blue. Informational notes are not defects.
export function findingSeverity(finding: Pick<V2Finding, 'findingClass'>): DefectSeverity | null {
  switch (finding.findingClass) {
    case 'safety':
      return 'safety';
    case 'defect':
      return 'repair';
    case 'maintenance':
    case 'advisory':
      return 'maintenance';
    default:
      return null;
  }
}
